import type {
  MiniappInitialState,
  MiniappInstallShopifyForAgentIntegrationsOutput,
  MiniappIsFeatureEnabledOutput,
  MiniappRecordIntegrationRequestOutput,
  MiniappToParentNotification,
  MiniappToParentRequest,
  ParentToMiniappNotification,
  ParentToMiniappRequest,
  RequestAuthOutput,
} from "./generated/miniappRpc";
import type { SelectedElement } from "./generated/selectMode";

import { z } from "zod";

import {
  agentBillingPlanSchema,
  agentBillingSubscriptionIntervalSchema,
  featureTypeSchema,
} from "./agent-billing";
import { brandGenerationSessionMetadataSchema } from "./agentSession";
import { reploOpencodeHarnessUploadRegistryItemsToolInputSchema } from "./agentTools/reploOpencodeHarness";
import {
  agentUiStateSchema,
  agentUiViewportSchema,
  notificationSoundSettingsSchema,
} from "./bedrock";
import { uiMessagePartsSchema } from "./mentionable";
import {
  selectableDataSchema,
  selectedElementSchema,
  selectModeBoundingRectSchema,
  selectModeClickPositionSchema,
  selectModeInlineTextStatusSchema,
  selectModeSelectionRectSchema,
  selectModeTiptapActiveMarksSchema,
} from "./selectMode";
import { selectModeParentMessageSchema } from "./selectModeIframeRpc";
import { themeConfigSchema } from "./theme";
import { viewSchema } from "./views";

// Wire identity for the bedrock miniapp <-> agent-ui iframe RPC channel.
export const MINIAPP_RPC_CHANNEL_ID = "replo:miniapp-rpc" as const;
export const MINIAPP_RPC_CHANNEL_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Shared method-arg schemas
// ---------------------------------------------------------------------------

const setThemeCssVarsInputSchema = z.object({
  cssVars: z.record(z.string(), z.string()),
});
const setThemeConfigParentInputSchema = z.object({
  themeConfig: themeConfigSchema.nullable(),
});
const setThemeConfigInputSchema = z.object({
  themeConfig: themeConfigSchema,
});

const setCurrentRouteInputSchema = z.object({ route: z.string() });
// Whether this miniapp's tab is the foreground/active one. Background tabs stay
// mounted (display:none), so a miniapp can't tell on its own; the parent relays
// it here so tab-scoped side effects (e.g. reclaiming a stopped dev server) only
// run on the visible tab.
const setTabActiveInputSchema = z.object({ isActive: z.boolean() });
export const registryShareRecordSchema = z
  .object({
    type: z.enum(["page", "skill", "plan", "branding"]),
    sourceType: z.string().min(1).max(80),
    sourceKey: z.string().min(1).max(512),
    itemId: z.string().min(1),
    name: z.string().min(1),
    shareUrl: z.url(),
    updatedAt: z.string().min(1),
  })
  .describe("MiniappRegistryShareRecord");
export const registryShareResultSchema = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("success"),
      record: registryShareRecordSchema,
    }),
    z.object({
      status: z.literal("failed"),
      type: z.enum(["page", "skill", "plan", "branding"]),
      sourceType: z.string().min(1).max(80),
      sourceKey: z.string().min(1).max(512),
      message: z.string().min(1).max(500),
      failedAt: z.string().min(1),
    }),
  ])
  .describe("MiniappRegistryShareResult");
const updateViewportInputSchema = z.object({ viewport: agentUiViewportSchema });
// The miniapp builds message parts with the same `@replohq/bedrock-sdk`
// primitives the agent-ui composer uses (`createMentionableContext` +
// `resolveMentionable`), so the payload is the shared `UIMessagePart[]` wire
// shape. The parent forwards these into the identical `sendMessage` mutation
// after prepending its own authoritative `prompt-source` provenance chip.
export const sendMessageToMainSessionInputSchema = z
  .object({
    parts: uiMessagePartsSchema,
    // Which chat session the message lands in. "current" posts into the user's
    // open main chat so the in-context agent drives the work and narrates it
    // inline (provisioning); "new" starts a fresh session for flows that begin
    // their own task (e.g. a new skill). Defaults to "new" to preserve callers
    // that predate this field.
    targetSession: z.enum(["current", "new"]).optional(),
    // (Noah, 2026-07-11, REPL-29203): Tags the resulting main session so a
    // miniapp can re-identify it from session state (e.g. Brand Studio deriving
    // its generating spinner, surviving a page refresh).
    brandGeneration: brandGenerationSessionMetadataSchema.optional(),
  })
  .describe("SendMessageToMainSessionInput");
const selectModeHoverChangedInputSchema = z.object({
  rect: selectModeBoundingRectSchema.nullable(),
});
const selectModeSelectionChangedInputSchema = z.object({
  data: selectableDataSchema.nullable(),
});
const selectModeAreaSelectionChangedInputSchema = z.object({
  rect: selectModeBoundingRectSchema,
  items: z.array(selectableDataSchema),
});
const selectModeDragRectChangedInputSchema = z.object({
  dragRect: selectModeBoundingRectSchema.nullable(),
  selectionRects: z.array(selectModeSelectionRectSchema),
});
const selectModeInlineEditStateChangedInputSchema = z.object({
  isEditing: z.boolean(),
  isPending: z.boolean().optional(),
  activeMarks: selectModeTiptapActiveMarksSchema,
});
const selectModeInlineTextStatusChangedInputSchema = z.object({
  status: selectModeInlineTextStatusSchema,
  // The dictionary dot-path (`hero.title`) when status is "translated".
  translationKey: z.string().nullable(),
});
// Region (in the mini-app iframe's own viewport coordinates) that select-mode
// overlays may paint into — e.g. the live preview area below a mini-app's own
// toolbar. The parent adds the mini-app iframe offset to position its clip
// layer. `null` clears the boundary so the overlay falls back to the whole
// iframe box.
const reportSelectModeBoundaryInputSchema = z.object({
  rect: selectModeBoundingRectSchema.nullable(),
});
const selectModeRectUpdatedInputSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("element"),
    rect: selectModeBoundingRectSchema,
    clickPosition: selectModeClickPositionSchema,
  }),
  z.object({
    type: z.literal("area"),
    rect: selectModeBoundingRectSchema,
  }),
]);
const insertTextIntoChatInputSchema = z.object({
  text: z.string().min(1).max(50_000),
  // (Ezra, 2026-07-01, REPL-28559): when set, the parent skips the insert
  // (and the chat surface swap) if the composer already has a draft, so the
  // text never mashes into existing input.
  onlyIfEmpty: z.boolean().optional(),
});
const openAgentUiTabInputSchema = z.object({ route: z.string().min(1) });
// Cross-miniapp navigation: a miniapp asks the parent to open a different miniapp at a typed product destination (e.g. Files handing a `.tldr` to Draw); reuses the agent-facing View contract so there's one source of truth, shared by the agent's manage-tabs tool.
const openMiniappViewInputSchema = z.object({ view: viewSchema });
// Passive route reporting: the miniapp tells the parent its current internal
// route so the parent tab's URL stays in sync. Unlike `openAgentUiTab`, this
// never changes which tab is active. The root route serializes to "/", so the
// route is not constrained to be non-empty.
const reportMiniappRouteInputSchema = z.object({ route: z.string() });
const setMiniappTabTitleInputSchema = z.object({
  title: z.string().max(200).nullable(),
});
const openExternalUrlInputSchema = z.object({
  url: z.url(),
  target: z.enum(["top", "blank"]).default("top"),
});
const openChatSessionInputSchema = z.object({ sessionId: z.uuid() });
const openFileViewerInputSchema = z.object({
  fileUrl: z.url(),
  fileName: z.string().min(1),
  fileMime: z.string().min(1),
});
const insertSkillIntoChatInputSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  filepath: z.string().min(1),
});
// Miniapp-contributed cmd-k shortcut items. The miniapp sends serializable
// descriptors only — `onSelect` and component icons can't cross postMessage —
// so the host renders them in its command palette and posts `shortcutInvoked`
// back with the item id when the user selects one.
export const serializableShortcutIconSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("emoji"), char: z.string().min(1).max(8) }),
    z.object({ type: z.literal("icon"), name: z.string().min(1).max(80) }),
  ])
  .describe("SerializableShortcutIcon");
// Note (Noah, 2026-07-22): We only support one-level trees right now since
// they're harder to represent in schemas. Deeper trees can be added later; the
// host command menu already supports recursive `children` frames.
export const serializableShortcutChildItemSchema = z
  .object({
    id: z.string().min(1).max(120),
    label: z.string().min(1).max(120),
    description: z.string().max(200).optional(),
    icon: serializableShortcutIconSchema.optional(),
  })
  .describe("SerializableShortcutChildItem");
export const serializableShortcutItemSchema = z
  .object({
    id: z.string().min(1).max(120),
    label: z.string().min(1).max(120),
    // Optional sub-group within the miniapp's command group; the host renders
    // it dash-separated after the miniapp name, e.g. "Assets - Actions".
    group: z.string().min(1).max(80).optional(),
    description: z.string().max(200).optional(),
    icon: serializableShortcutIconSchema.optional(),
    children: z.array(serializableShortcutChildItemSchema).optional(),
  })
  .describe("SerializableShortcutItem");
const registerShortcutItemsInputSchema = z.object({
  items: z.array(serializableShortcutItemSchema),
});
const unregisterShortcutItemsInputSchema = z.object({
  itemIds: z.array(z.string().min(1)),
});
const shortcutInvokedInputSchema = z.object({ itemId: z.string().min(1) });
// `variant` is optional so callers can send a plain informational
// notification without choosing a severity; the parent defaults to "info".
const sendNotificationInputSchema = z.object({
  variant: z.enum(["info", "success", "warning", "error"]).optional(),
  title: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
});
export const trackEventInputSchema = z
  .object({
    name: z.string().min(1).max(120),
    properties: z.record(z.string(), z.unknown()),
  })
  .describe("MiniappTrackEventInput");
// Faro performance measurement (mini-app → parent's Faro instance).
const pushMeasurementInputSchema = z.object({
  type: z.string().min(1).max(120),
  values: z.record(z.string(), z.number()),
  context: z.record(z.string(), z.string()).optional(),
});
const shareRegistryItemInputSchema = z
  .object({
    type: z.enum(["page", "skill", "plan", "branding"]),
    sourceType: z.string().min(1).max(80),
    sourceKey: z.string().min(1).max(512),
    name: z.string().min(1),
    // Structured upload-registry-items input the coordinator execs (no LLM turn).
    // The fields above identify the record so the parent can match the CLI's
    // result back to the popover.
    uploadInput: reploOpencodeHarnessUploadRegistryItemsToolInputSchema,
  })
  // The parent matches the created item by `type`, so `type` must be among the
  // published `uploadInput.types` or a successful share reports a false failure.
  .refine(
    (input) => {
      return (
        input.uploadInput.types === null ||
        input.uploadInput.types.includes(input.type)
      );
    },
    { message: "shareRegistryItem.type must be one of uploadInput.types" },
  );

// Billing intents — see Step 1 in the agent-ui agent-billing modals plan.
// Callers express intent only; the shell composes the eventual SubscriptionView.
// `changePlan` requires at least one of `targetPlan`, `interval`, or `addOns`;
// the empty payload `{}` is rejected at the schema layer so the host can't be
// asked to open a "no target" modal until a proper plan picker UX exists.
export const billingChangePlanIntentSchema = z
  .object({
    targetPlan: agentBillingPlanSchema.optional(),
    interval: agentBillingSubscriptionIntervalSchema.optional(),
    addOns: z
      .object({
        sites: z.number().int().nonnegative().optional(),
        themeProjects: z.number().int().nonnegative().optional(),
        publishedElements: z.number().int().nonnegative().optional(),
      })
      .optional(),
  })
  .refine(
    (intent) => {
      return (
        intent.targetPlan !== undefined ||
        intent.interval !== undefined ||
        intent.addOns !== undefined
      );
    },
    {
      message:
        "billingChangePlan requires at least one of targetPlan, interval, or addOns",
    },
  )
  .describe("BillingChangePlanIntent");

const billingResumePlanIntentSchema = z
  .object({})
  .describe("BillingResumePlanIntent");

const billingAddCapacityIntentSchema = z
  .object({
    count: z.number().int().positive().optional(),
  })
  .describe("BillingAddCapacityIntent");

const billingAddCreditsIntentSchema = z
  .object({
    featureType: featureTypeSchema.optional(),
  })
  .describe("BillingAddCreditsIntent");

// Opens the shell-owned Domains surface with optional search and site intent.
const domainsOpenIntentSchema = z
  .object({
    view: z.enum(["search", "manage"]).optional(),
    query: z.string().optional(),
    domain: z.string().optional(),
    siteId: z.string().optional(),
  })
  .describe("DomainsOpenIntent");
export const miniappRecordIntegrationRequestInputSchema = z
  .object({
    integrationName: z.string().trim().min(1).max(120),
  })
  .describe("MiniappRecordIntegrationRequestInput");
export const miniappRecordIntegrationRequestOutputSchema = z
  .object({})
  .describe("MiniappRecordIntegrationRequestOutput");
export const miniappInstallShopifyForAgentIntegrationsInputSchema = z
  .object({
    projectId: z.string().min(1),
  })
  .describe("MiniappInstallShopifyForAgentIntegrationsInput");
export const miniappInstallShopifyForAgentIntegrationsOutputSchema = z
  .object({})
  .describe("MiniappInstallShopifyForAgentIntegrationsOutput");
export const miniappIsFeatureEnabledInputSchema = z
  .object({
    featureName: z.string().min(1),
  })
  .describe("MiniappIsFeatureEnabledInput");
export const miniappIsFeatureEnabledOutputSchema = z
  .object({ enabled: z.boolean() })
  .describe("MiniappIsFeatureEnabledOutput");
export const requestAuthOutputSchema = z
  .object({ token: z.string() })
  .describe("RequestAuthOutput");
export const miniappServiceUrlsSchema = z
  .object({
    bedrockApiUrl: z.url(),
    agentCoordinatorBaseUrl: z.url(),
  })
  .describe("MiniappServiceUrls");
// Snapshot of every parent-owned state channel, answered to `getInitialState`
// on miniapp boot. Field shapes mirror the corresponding `set*` push payloads.
export const miniappInitialStateSchema = z
  .object({
    route: z.string(),
    themeCssVars: z.record(z.string(), z.string()),
    themeConfig: themeConfigSchema.nullable(),
    agentUiState: agentUiStateSchema,
    serviceUrls: miniappServiceUrlsSchema.optional(),
    isTabActive: z.boolean(),
  })
  .describe("MiniappInitialState");

// ---------------------------------------------------------------------------
// Invocation schemas
// ---------------------------------------------------------------------------
//
// Each variant pairs a `method` literal with its input schema, so one
// `safeParse` rejects unknown methods AND mismatched payloads. Inbound
// dispatchers parse `{ method, data }` once and switch on `parsed.data.method`
// with `data` already narrowed to that method's input type.

export const parentToMiniappMethodConfig = {
  setThemeCssVars: {
    inputSchema: setThemeCssVarsInputSchema,
    outputSchema: z.void(),
  },
  setThemeConfig: {
    inputSchema: setThemeConfigParentInputSchema,
    outputSchema: z.void(),
  },
  setAgentUiState: { inputSchema: agentUiStateSchema, outputSchema: z.void() },
  setCurrentRoute: {
    inputSchema: setCurrentRouteInputSchema,
    outputSchema: z.void(),
  },
  setTabActive: {
    inputSchema: setTabActiveInputSchema,
    outputSchema: z.void(),
  },
  registryShareUpdated: {
    inputSchema: registryShareRecordSchema,
    outputSchema: z.void(),
  },
  registryShareResult: {
    inputSchema: registryShareResultSchema,
    outputSchema: z.void(),
  },
  shortcutInvoked: {
    inputSchema: shortcutInvokedInputSchema,
    outputSchema: z.void(),
  },
  // Editor-originated select-mode messages destined for the nested canopy
  // preview iframe. The miniapp can't be reached by the editor's optimistic
  // updates directly — it relays this payload down to canopy verbatim.
  selectModeRelayMessage: {
    inputSchema: selectModeParentMessageSchema,
    outputSchema: z.void(),
  },

  symbolicateSelectModeElement: {
    inputSchema: selectedElementSchema,
    outputSchema: selectedElementSchema,
    timeoutMs: 1_500,
  },
};
export const parentToMiniappNotificationSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("setThemeCssVars"),
      data: parentToMiniappMethodConfig.setThemeCssVars.inputSchema,
    }),
    z.object({
      method: z.literal("setThemeConfig"),
      data: parentToMiniappMethodConfig.setThemeConfig.inputSchema,
    }),
    z.object({
      method: z.literal("setAgentUiState"),
      data: parentToMiniappMethodConfig.setAgentUiState.inputSchema,
    }),
    z.object({
      method: z.literal("setCurrentRoute"),
      data: parentToMiniappMethodConfig.setCurrentRoute.inputSchema,
    }),
    z.object({
      method: z.literal("setTabActive"),
      data: parentToMiniappMethodConfig.setTabActive.inputSchema,
    }),
    z.object({
      method: z.literal("registryShareUpdated"),
      data: parentToMiniappMethodConfig.registryShareUpdated.inputSchema,
    }),
    z.object({
      method: z.literal("registryShareResult"),
      data: parentToMiniappMethodConfig.registryShareResult.inputSchema,
    }),
    z.object({
      method: z.literal("shortcutInvoked"),
      data: parentToMiniappMethodConfig.shortcutInvoked.inputSchema,
    }),
    z.object({
      method: z.literal("selectModeRelayMessage"),
      data: parentToMiniappMethodConfig.selectModeRelayMessage.inputSchema,
    }),
  ])
  .describe("ParentToMiniappNotification");
export const parentToMiniappRequestSchema = z
  .object({
    method: z.literal("symbolicateSelectModeElement"),
    data: parentToMiniappMethodConfig.symbolicateSelectModeElement.inputSchema,
  })
  .describe("ParentToMiniappRequest");
export const parentToMiniappResponseSchema = z.never();

export const agentSidebarNavigationSettingsSchema = z
  .object({
    enabled: z.boolean(),
  })
  .describe("AgentSidebarNavigationSettings");

export const miniappToParentMethodConfig = {
  notifyReady: { inputSchema: z.undefined(), outputSchema: z.void() },
  notifyStartupReady: { inputSchema: z.undefined(), outputSchema: z.void() },
  notifyStartupLoading: { inputSchema: z.undefined(), outputSchema: z.void() },
  updateViewport: {
    inputSchema: updateViewportInputSchema,
    outputSchema: z.void(),
  },
  sendMessageToMainSession: {
    inputSchema: sendMessageToMainSessionInputSchema,
    outputSchema: z.void(),
  },
  selectModeHoverChanged: {
    inputSchema: selectModeHoverChangedInputSchema,
    outputSchema: z.void(),
  },
  selectModeSelectionChanged: {
    inputSchema: selectModeSelectionChangedInputSchema,
    outputSchema: z.void(),
  },
  selectModeAreaSelectionChanged: {
    inputSchema: selectModeAreaSelectionChangedInputSchema,
    outputSchema: z.void(),
  },
  selectModeDragRectChanged: {
    inputSchema: selectModeDragRectChangedInputSchema,
    outputSchema: z.void(),
  },
  selectModeRectUpdated: {
    inputSchema: selectModeRectUpdatedInputSchema,
    outputSchema: z.void(),
  },
  selectModeClearSelection: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  selectModeInlineEditStateChanged: {
    inputSchema: selectModeInlineEditStateChangedInputSchema,
    outputSchema: z.void(),
  },
  selectModeInlineTextStatusChanged: {
    inputSchema: selectModeInlineTextStatusChangedInputSchema,
    outputSchema: z.void(),
  },
  reportSelectModeBoundary: {
    inputSchema: reportSelectModeBoundaryInputSchema,
    outputSchema: z.void(),
  },
  insertTextIntoChat: {
    inputSchema: insertTextIntoChatInputSchema,
    outputSchema: z.void(),
  },
  openAgentUiTab: {
    inputSchema: openAgentUiTabInputSchema,
    outputSchema: z.void(),
  },
  openMiniappView: {
    inputSchema: openMiniappViewInputSchema,
    outputSchema: z.void(),
  },
  reportMiniappRoute: {
    inputSchema: reportMiniappRouteInputSchema,
    outputSchema: z.void(),
  },
  setMiniappTabTitle: {
    inputSchema: setMiniappTabTitleInputSchema,
    outputSchema: z.void(),
  },
  openExternalUrl: {
    inputSchema: openExternalUrlInputSchema,
    outputSchema: z.void(),
  },
  openChatSession: {
    inputSchema: openChatSessionInputSchema,
    outputSchema: z.void(),
  },
  openFileViewer: {
    inputSchema: openFileViewerInputSchema,
    outputSchema: z.void(),
  },
  invalidateSandboxSkills: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  // (Max, 2026-07-02, REPL-27519): Miniapp changed project data server-side
  // (e.g. workspace transfer); host refetches its loaded project state.
  invalidateProjectData: { inputSchema: z.undefined(), outputSchema: z.void() },
  navigateHome: { inputSchema: z.undefined(), outputSchema: z.void() },
  logout: { inputSchema: z.undefined(), outputSchema: z.void() },
  insertSkillIntoChat: {
    inputSchema: insertSkillIntoChatInputSchema,
    outputSchema: z.void(),
  },
  setThemeConfig: {
    inputSchema: setThemeConfigInputSchema,
    outputSchema: z.void(),
  },
  resetThemeConfig: { inputSchema: z.undefined(), outputSchema: z.void() },
  setNotificationSoundSettings: {
    inputSchema: notificationSoundSettingsSchema,
    outputSchema: z.void(),
  },
  setAgentSidebarNavigationEnabled: {
    inputSchema: agentSidebarNavigationSettingsSchema,
    outputSchema: z.void(),
  },
  shareRegistryItem: {
    inputSchema: shareRegistryItemInputSchema,
    outputSchema: z.void(),
  },
  registerShortcutItems: {
    inputSchema: registerShortcutItemsInputSchema,
    outputSchema: z.void(),
  },
  unregisterShortcutItems: {
    inputSchema: unregisterShortcutItemsInputSchema,
    outputSchema: z.void(),
  },
  sendNotification: {
    inputSchema: sendNotificationInputSchema,
    outputSchema: z.void(),
  },
  trackEvent: { inputSchema: trackEventInputSchema, outputSchema: z.void() },
  pushMeasurement: {
    inputSchema: pushMeasurementInputSchema,
    outputSchema: z.void(),
  },
  billingChangePlan: {
    inputSchema: billingChangePlanIntentSchema,
    outputSchema: z.void(),
  },
  billingResumePlan: {
    inputSchema: billingResumePlanIntentSchema,
    outputSchema: z.void(),
  },
  billingAddSites: {
    inputSchema: billingAddCapacityIntentSchema,
    outputSchema: z.void(),
  },
  billingAddThemeProjects: {
    inputSchema: billingAddCapacityIntentSchema,
    outputSchema: z.void(),
  },
  billingAddPublishedElements: {
    inputSchema: billingAddCapacityIntentSchema,
    outputSchema: z.void(),
  },
  billingAddCredits: {
    inputSchema: billingAddCreditsIntentSchema,
    outputSchema: z.void(),
  },
  billingEditSpendSettings: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  billingCancelPlan: { inputSchema: z.undefined(), outputSchema: z.void() },
  domainsOpen: {
    inputSchema: domainsOpenIntentSchema,
    outputSchema: z.void(),
  },

  getInitialState: {
    inputSchema: z.undefined(),
    outputSchema: miniappInitialStateSchema,
  },
  requestAuth: {
    inputSchema: z.undefined(),
    outputSchema: requestAuthOutputSchema,
  },
  recordIntegrationRequest: {
    inputSchema: miniappRecordIntegrationRequestInputSchema,
    outputSchema: miniappRecordIntegrationRequestOutputSchema,
  },
  installShopifyForAgentIntegrations: {
    inputSchema: miniappInstallShopifyForAgentIntegrationsInputSchema,
    outputSchema: miniappInstallShopifyForAgentIntegrationsOutputSchema,
  },
  isFeatureEnabled: {
    inputSchema: miniappIsFeatureEnabledInputSchema,
    outputSchema: miniappIsFeatureEnabledOutputSchema,
  },
  getAgentSidebarNavigationSettings: {
    inputSchema: z.undefined(),
    outputSchema: agentSidebarNavigationSettingsSchema,
  },
};
export const miniappToParentNotificationSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("notifyReady"),
    }),
    z.object({
      method: z.literal("notifyStartupReady"),
    }),
    z.object({
      method: z.literal("notifyStartupLoading"),
    }),
    z.object({
      method: z.literal("updateViewport"),
      data: miniappToParentMethodConfig.updateViewport.inputSchema,
    }),
    z.object({
      method: z.literal("sendMessageToMainSession"),
      data: miniappToParentMethodConfig.sendMessageToMainSession.inputSchema,
    }),
    z.object({
      method: z.literal("selectModeHoverChanged"),
      data: miniappToParentMethodConfig.selectModeHoverChanged.inputSchema,
    }),
    z.object({
      method: z.literal("selectModeSelectionChanged"),
      data: miniappToParentMethodConfig.selectModeSelectionChanged.inputSchema,
    }),
    z.object({
      method: z.literal("selectModeAreaSelectionChanged"),
      data: miniappToParentMethodConfig.selectModeAreaSelectionChanged
        .inputSchema,
    }),
    z.object({
      method: z.literal("selectModeDragRectChanged"),
      data: miniappToParentMethodConfig.selectModeDragRectChanged.inputSchema,
    }),
    z.object({
      method: z.literal("selectModeRectUpdated"),
      data: miniappToParentMethodConfig.selectModeRectUpdated.inputSchema,
    }),
    z.object({
      method: z.literal("selectModeClearSelection"),
    }),
    z.object({
      method: z.literal("selectModeInlineEditStateChanged"),
      data: miniappToParentMethodConfig.selectModeInlineEditStateChanged
        .inputSchema,
    }),
    z.object({
      method: z.literal("selectModeInlineTextStatusChanged"),
      data: miniappToParentMethodConfig.selectModeInlineTextStatusChanged
        .inputSchema,
    }),
    z.object({
      method: z.literal("reportSelectModeBoundary"),
      data: miniappToParentMethodConfig.reportSelectModeBoundary.inputSchema,
    }),
    z.object({
      method: z.literal("insertTextIntoChat"),
      data: miniappToParentMethodConfig.insertTextIntoChat.inputSchema,
    }),
    z.object({
      method: z.literal("openAgentUiTab"),
      data: miniappToParentMethodConfig.openAgentUiTab.inputSchema,
    }),
    z.object({
      method: z.literal("openMiniappView"),
      data: miniappToParentMethodConfig.openMiniappView.inputSchema,
    }),
    z.object({
      method: z.literal("reportMiniappRoute"),
      data: miniappToParentMethodConfig.reportMiniappRoute.inputSchema,
    }),
    z.object({
      method: z.literal("setMiniappTabTitle"),
      data: miniappToParentMethodConfig.setMiniappTabTitle.inputSchema,
    }),
    z.object({
      method: z.literal("openExternalUrl"),
      data: miniappToParentMethodConfig.openExternalUrl.inputSchema,
    }),
    z.object({
      method: z.literal("openChatSession"),
      data: miniappToParentMethodConfig.openChatSession.inputSchema,
    }),
    z.object({
      method: z.literal("openFileViewer"),
      data: miniappToParentMethodConfig.openFileViewer.inputSchema,
    }),
    z.object({
      method: z.literal("invalidateSandboxSkills"),
    }),
    z.object({
      method: z.literal("invalidateProjectData"),
    }),
    z.object({
      method: z.literal("navigateHome"),
    }),
    z.object({
      method: z.literal("logout"),
    }),
    z.object({
      method: z.literal("insertSkillIntoChat"),
      data: miniappToParentMethodConfig.insertSkillIntoChat.inputSchema,
    }),
    z.object({
      method: z.literal("setThemeConfig"),
      data: miniappToParentMethodConfig.setThemeConfig.inputSchema,
    }),
    z.object({
      method: z.literal("resetThemeConfig"),
    }),
    z.object({
      method: z.literal("setNotificationSoundSettings"),
      data: miniappToParentMethodConfig.setNotificationSoundSettings
        .inputSchema,
    }),
    z.object({
      method: z.literal("setAgentSidebarNavigationEnabled"),
      data: miniappToParentMethodConfig.setAgentSidebarNavigationEnabled
        .inputSchema,
    }),
    z.object({
      method: z.literal("shareRegistryItem"),
      data: miniappToParentMethodConfig.shareRegistryItem.inputSchema,
    }),
    z.object({
      method: z.literal("registerShortcutItems"),
      data: miniappToParentMethodConfig.registerShortcutItems.inputSchema,
    }),
    z.object({
      method: z.literal("unregisterShortcutItems"),
      data: miniappToParentMethodConfig.unregisterShortcutItems.inputSchema,
    }),
    z.object({
      method: z.literal("sendNotification"),
      data: miniappToParentMethodConfig.sendNotification.inputSchema,
    }),
    z.object({
      method: z.literal("trackEvent"),
      data: miniappToParentMethodConfig.trackEvent.inputSchema,
    }),
    z.object({
      method: z.literal("pushMeasurement"),
      data: miniappToParentMethodConfig.pushMeasurement.inputSchema,
    }),
    z.object({
      method: z.literal("billingChangePlan"),
      data: miniappToParentMethodConfig.billingChangePlan.inputSchema,
    }),
    z.object({
      method: z.literal("billingResumePlan"),
      data: miniappToParentMethodConfig.billingResumePlan.inputSchema,
    }),
    z.object({
      method: z.literal("billingAddSites"),
      data: miniappToParentMethodConfig.billingAddSites.inputSchema,
    }),
    z.object({
      method: z.literal("billingAddThemeProjects"),
      data: miniappToParentMethodConfig.billingAddThemeProjects.inputSchema,
    }),
    z.object({
      method: z.literal("billingAddPublishedElements"),
      data: miniappToParentMethodConfig.billingAddPublishedElements.inputSchema,
    }),
    z.object({
      method: z.literal("billingAddCredits"),
      data: miniappToParentMethodConfig.billingAddCredits.inputSchema,
    }),
    z.object({
      method: z.literal("billingEditSpendSettings"),
    }),
    z.object({
      method: z.literal("billingCancelPlan"),
    }),
    z.object({
      method: z.literal("domainsOpen"),
      data: miniappToParentMethodConfig.domainsOpen.inputSchema,
    }),
  ])
  .describe("MiniappToParentNotification");

export const miniappToParentRequestSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("getInitialState"),
      data: miniappToParentMethodConfig.getInitialState.inputSchema,
    }),
    z.object({
      method: z.literal("requestAuth"),
      data: miniappToParentMethodConfig.requestAuth.inputSchema,
    }),
    z.object({
      method: z.literal("recordIntegrationRequest"),
      data: miniappToParentMethodConfig.recordIntegrationRequest.inputSchema,
    }),
    z.object({
      method: z.literal("installShopifyForAgentIntegrations"),
      data: miniappToParentMethodConfig.installShopifyForAgentIntegrations
        .inputSchema,
    }),
    z.object({
      method: z.literal("isFeatureEnabled"),
      data: miniappToParentMethodConfig.isFeatureEnabled.inputSchema,
    }),
    z.object({
      method: z.literal("getAgentSidebarNavigationSettings"),
      data: miniappToParentMethodConfig.getAgentSidebarNavigationSettings
        .inputSchema,
    }),
  ])
  .describe("MiniappToParentRequest");
export const miniappToParentResponseSchema = z.never();

export type MiniappToParentNotificationInputOf<
  MethodType extends MiniappToParentNotification["method"],
> =
  Extract<MiniappToParentNotification, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;
export type MiniappToParentNotificationRemote = {
  [MethodType in MiniappToParentNotification["method"]]: (
    input: MiniappToParentNotificationInputOf<MethodType>,
  ) => void;
};

type MiniappToParentRequestConfig = Pick<
  typeof miniappToParentMethodConfig,
  | "getInitialState"
  | "requestAuth"
  | "recordIntegrationRequest"
  | "installShopifyForAgentIntegrations"
  | "isFeatureEnabled"
>;
export type MiniappToParentRequestMethodName =
  keyof MiniappToParentRequestConfig;
export type MiniappToParentRequestInputOf<
  MethodType extends MiniappToParentRequestMethodName,
> =
  Extract<MiniappToParentRequest, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;

// NOTE (Noah, 2026-05-13, REPL-26984): Maps each request method name to its
// generated output type, avoiding z.infer in favor of the generated types system.
type MiniappToParentRequestOutputMap = {
  getInitialState: MiniappInitialState;
  requestAuth: RequestAuthOutput;
  recordIntegrationRequest: MiniappRecordIntegrationRequestOutput;
  installShopifyForAgentIntegrations: MiniappInstallShopifyForAgentIntegrationsOutput;
  isFeatureEnabled: MiniappIsFeatureEnabledOutput;
};
export type MiniappToParentRequestOutputOf<
  MethodType extends MiniappToParentRequestMethodName,
> = MiniappToParentRequestOutputMap[MethodType];
export type MiniappToParentRequestOutput = {
  [MethodType in MiniappToParentRequestMethodName]: MiniappToParentRequestOutputOf<MethodType>;
}[MiniappToParentRequestMethodName];
export type MiniappToParentRequestRemote = {
  [MethodType in MiniappToParentRequestMethodName]: (
    input: MiniappToParentRequestInputOf<MethodType>,
  ) => Promise<MiniappToParentRequestOutputOf<MethodType>>;
};

export type ParentToMiniappNotificationInputOf<
  MethodType extends ParentToMiniappNotification["method"],
> =
  Extract<ParentToMiniappNotification, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;
export type ParentToMiniappNotificationRemote = {
  [MethodType in ParentToMiniappNotification["method"]]: (
    input: ParentToMiniappNotificationInputOf<MethodType>,
  ) => void;
};

type ParentToMiniappRequestConfig = Pick<
  typeof parentToMiniappMethodConfig,
  "symbolicateSelectModeElement"
>;
export type ParentToMiniappRequestMethodName =
  keyof ParentToMiniappRequestConfig;
export type ParentToMiniappRequestInputOf<
  MethodType extends ParentToMiniappRequestMethodName,
> = Extract<ParentToMiniappRequest, { method: MethodType }>["data"];
export type ParentToMiniappRequestOutputOf<
  MethodType extends ParentToMiniappRequestMethodName,
> = MethodType extends "symbolicateSelectModeElement" ? SelectedElement : never;
export type ParentToMiniappRequestOutput = {
  [MethodType in ParentToMiniappRequestMethodName]: ParentToMiniappRequestOutputOf<MethodType>;
}[ParentToMiniappRequestMethodName];
export type ParentToMiniappRequestRemote = {
  [MethodType in ParentToMiniappRequestMethodName]: (
    input: ParentToMiniappRequestInputOf<MethodType>,
  ) => Promise<ParentToMiniappRequestOutputOf<MethodType>>;
};
